import { PageContent } from "@/lib/interfaces/globalInterfaces";
import { createSlice, PayloadAction, createAsyncThunk } from "@reduxjs/toolkit";
import { fetchPageContent } from "@/lib/strapi/fetchPageContent";

export interface GlobalState {
  language: string;
  languageCode: string;
  pageContents: Record<string, PageContent | null>;
  status: "idle" | "loading" | "succeeded" | "failed";
  error: string | null;
  givenName: string;
  surname: string;
  registeredEmail: string;
  enteredEmail: string;
  transactionId: string;
  companyName: string;
  lastVerifiedDate: string;
  reverificationDueDate: string;
  actionType: "REVERIFY" | "MODIFY" | null;
  isProfileLoaded: boolean;
  channel?: string | null; // 'mweb' or 'mapp' when coming from m.com web/app
}

const initialState: GlobalState = {
  language: "English",
  languageCode: "en",
  pageContents: {},
  status: "idle",
  error: null,
  givenName: "",
  surname: "",
  registeredEmail: "",
  enteredEmail: "",
  transactionId: "",
  companyName: "",
  lastVerifiedDate: "",
  reverificationDueDate: "",
  actionType: null,
  isProfileLoaded: false,
  channel: null,
};

// Generic thunk for fetching page content
export const fetchPageContentThunk = createAsyncThunk(
  "global/fetchPageContent",
  async ({ languageCode, slug }: { slug: string; languageCode: string }) => {
    const data = await fetchPageContent(languageCode, slug);
    return { slug, languageCode, data };
  },
);

const globalSlice = createSlice({
  name: "global",
  initialState,
  reducers: {
    setRegisteredEmail(state, action: PayloadAction<string>) {
      state.registeredEmail = action.payload;
    },
    setGivenName(state, action: PayloadAction<string>) {
      state.givenName = action.payload;
    },
    setSurname(state, action: PayloadAction<string>) {
      state.surname = action.payload;
    },
    setGivenAndSurName(
      state,
      action: PayloadAction<{ givenName: string; surname: string }>,
    ) {
      state.givenName = action.payload.givenName;
      state.surname = action.payload.surname;
    },
    setEnteredEmail(state, action: PayloadAction<string>) {
      state.enteredEmail = action.payload;
    },
    setTransactionId(state, action: PayloadAction<string>) {
      state.transactionId = action.payload;
    },
    setCompanyName(state, action: PayloadAction<string>) {
      state.companyName = action.payload;
    },
    setLastVerifiedDate(state, action: PayloadAction<string>) {
      state.lastVerifiedDate = action.payload;
    },
    setReverificationDueDate(state, action: PayloadAction<string>) {
      state.reverificationDueDate = action.payload;
    },
    setActionType(state, action: PayloadAction<"REVERIFY" | "MODIFY" | null>) {
      state.actionType = action.payload;
    },
    setProfileLoaded(state, action) {
      state.isProfileLoaded = action.payload;
    },
    //clearVerificationState: () => initialState,

    //  NEW SAFE RESET
    resetVerificationFlow(state) {
      state.enteredEmail = "";
      state.transactionId = "";
      state.actionType = null;
      state.isProfileLoaded = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPageContentThunk.pending, (state) => {
        state.status = "loading";
      })
      .addCase(fetchPageContentThunk.fulfilled, (state, action) => {
        state.status = "succeeded";
        const cacheKey = `${action.payload.slug}::${action.payload.languageCode}`;
        state.pageContents[cacheKey] = action.payload.data;
      })
      .addCase(fetchPageContentThunk.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message ?? "Something went wrong";
      });
  },
});

export const {
  setCompanyName,
  setGivenName,
  setSurname,
  setEnteredEmail,
  setRegisteredEmail,
  setTransactionId,
  setActionType,
  setLastVerifiedDate,
  setReverificationDueDate,
  setProfileLoaded,
  resetVerificationFlow,
  setGivenAndSurName,
} = globalSlice.actions;
export default globalSlice.reducer;
